/**
 * Login component
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { check_user_auth, local_check_user_auth, user_login } from '@rpt-ngrx/actions/session.action';
import { force_disable_content_loader } from '@rpt-ngrx/actions/ui.action';
import { selectAuth, selectIsIframeMode } from '@rpt-ngrx/selectors/session.selector';
import { ThemeService } from '@shared/services/theme/theme.service';
import { IUser } from '@shared/types';
import { ILoginClient } from '@shared/types/login-client.model';

@Component({
    selector: 'login',
    styleUrls: ['./login.scss'],
    templateUrl: './login.template.html',
})
export class LoginComponent implements OnInit, OnDestroy {
    public client: ILoginClient = {
        email: '',
        password: '',
    };
    public errorMessage: string = '';
    public disabledButton: boolean = false;
    public isProduction = environment.production;
    public theme: string;
    private unsubscribe$: Subject<void> = new Subject<void>();
    /**
     * Initialize the component
     * Components need initialization in the constructor.
     */
    constructor(
        private router: Router,
        private store: Store,
        private themeService: ThemeService,
    ) {}

    /**
     * This function is called right after the directive's
     * data-bound properties have been checked for the first time,
     * and before any of its children have been checked.
     * It is invoked only once when the directive is instantiated.
     * In this function we subscribe to user session state changes.
     * And once user session is changed we check if it contains errors
     * and show them
     */
    public ngOnInit() {
        this.store
            .select(selectIsIframeMode)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isIframe) => {
                if (isIframe) {
                    this.store.dispatch(check_user_auth());
                } else {
                    this.store.dispatch(local_check_user_auth());
                }
            });
        this.store
            .select(selectAuth)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
                (data: { isAuthenticated: boolean; user: IUser }) => {
                    if (data.isAuthenticated) {
                        this.router.navigate(['/custom-reports', 'home']);
                    } else {
                        this.store.dispatch(force_disable_content_loader());
                    }
                },
                (error) => {
                    console.log(error);
                },
            );
        this.theme = this.themeService.getTheme();
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /**
     * Perform a login operation
     */
    public onSubmit() {
        this.disabledButton = true;
        this.errorMessage = '';
        const credentials = {
            user: this.client.email,
            password: this.client.password,
        };
        this.store.dispatch(user_login(credentials));
    }
}
